import type { Team, Gender, Ranking } from '@/types';

// Function to get player initials from full name
export const getPlayerInitials = (name: string): string => {
  // Split the name by spaces
  const nameParts = name.trim().split(/\s+/);

  if (nameParts.length === 0) return '';

  if (nameParts.length === 1) {
    // If there's only one name, return the first letter
    return nameParts[0].charAt(0).toUpperCase();
  }

  // Get first letter of first name and first letter of last name
  const firstInitial = nameParts[0].charAt(0);
  const lastInitial = nameParts[nameParts.length - 1].charAt(0);

  return (firstInitial + lastInitial).toUpperCase();
};

// Function to format phone number for WhatsApp
export const formatWhatsAppNumber = (phone: string) => {
  // Remove spaces, hyphens and parentheses
  const cleanNumber = phone.replace(/[\s\-()]/g, '');

  // If the number doesn't start with '+' or country code, assume it's from Argentina (54)
  if (!cleanNumber.startsWith('+') && !cleanNumber.startsWith('54')) {
    return `54${cleanNumber}`;
  }

  // If it starts with '+', remove it
  return cleanNumber.startsWith('+') ? cleanNumber.substring(1) : cleanNumber;
};

type HasRankings = { rankings?: Ranking[] };

// A player's ranking for one sport. Both User and Player carry the same Ranking payload.
export const getRankingForSport = (
  player: HasRankings,
  sportUid: string,
): Ranking | undefined =>
  player.rankings?.find((r) => r.sport?.uid === sportUid);

// Someone who signed up through the app and never answered the level questions has no ranking for
// that sport, and the backend rejects them: without Elo the fixture blows up when it seeds the teams.
export const hasEloForSport = (
  player: HasRankings,
  sportUid: string,
): boolean => !!getRankingForSport(player, sportUid);

// Function to get team average Elo
export const getTeamAverageElo = (team: Team, sportUid: string): number => {
  if (!team.players || team.players.length === 0) return 0;

  let totalElo = 0;
  let playersWithElo = 0;

  team.players.forEach((player) => {
    const sportRanking = getRankingForSport(player, sportUid);
    if (sportRanking && sportRanking.elo) {
      totalElo += sportRanking.elo;
      playersWithElo++;
    }
  });

  return playersWithElo > 0 ? Math.round(totalElo / playersWithElo) : 0;
};

// Function to get team dominant gender
export const getTeamGender = (team: Team): Gender | null => {
  if (!team.players || team.players.length === 0) return null;

  const genders = team.players
    .map((player) => player.gender as Gender)
    .filter(Boolean);

  if (genders.length === 0) return null;

  // If all players have the same gender
  if (genders.every((g) => g === genders[0])) return genders[0];

  // If mixed
  return 'MIXED' as Gender;
};
